import { useState, useEffect } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';

const emptyUser = { email: '', password: '', name: '', role: 'trader', branch_id: '', phone: '', status: 'active' };

export default function TradersPage() {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyUser);
  const [search, setSearch] = useState('');

  const fetchData = async () => {
    try {
      const [uRes, bRes] = await Promise.all([
        api.get('/users', { params: { role: 'trader' } }),
        api.get('/branches').catch(() => ({ data: [] }))
      ]);
      setUsers(uRes.data);
      setBranches(bRes.data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const openNew = () => { setEditing(null); setForm({...emptyUser}); setDialogOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, password: '', name: u.name, role: u.role, branch_id: u.branch_id || '', phone: u.phone || '', status: u.status });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {...form};
      if (!payload.password && editing) delete payload.password;
      if (editing) {
        await api.put(`/users/${editing.id}`, payload);
        toast.success('Trader updated');
      } else {
        if (!payload.password) payload.password = process.env.REACT_APP_DEFAULT_USER_PASSWORD || 'Password@123';
        await api.post('/users', payload);
        toast.success('Trader created');
      }
      setDialogOpen(false); fetchData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this trader?')) return;
    try { await api.delete(`/users/${id}`); toast.success('Trader deleted'); fetchData(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const filtered = users.filter(u => u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()));
  const getBranchName = (id) => branches.find(b => b.id === id)?.name || '-';

  return (
    <div className="space-y-6" data-testid="traders-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Traders</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Manage trader accounts and assignments</p>
        </div>
        <Button onClick={openNew} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="add-trader-btn">
          <Plus size={16} className="mr-2" /> Add Trader
        </Button>
      </div>
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-text-secondary" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search traders..." className="pl-9 bg-white border-brand-border rounded-sm" data-testid="trader-search-input" />
      </div>
      <Card className="border border-brand-border bg-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="traders-table">
              <thead>
                <tr className="border-b border-brand-border bg-brand-bg/50">
                  {['Name', 'Email', 'Phone', 'Branch', 'Status', 'Actions'].map(h => (
                    <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan={6} className="py-12 text-center text-brand-text-secondary">Loading...</td></tr> :
                 filtered.length === 0 ? <tr><td colSpan={6} className="py-12 text-center text-brand-text-secondary">No traders found</td></tr> :
                 filtered.map(u => (
                  <tr key={u.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                    <td className="py-3 px-4 font-medium text-brand-text-primary">{u.name}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{u.email}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{u.phone || '-'}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{getBranchName(u.branch_id)}</td>
                    <td className="py-3 px-4"><Badge className={`text-xs border-0 ${u.status === 'active' ? 'bg-brand-success/15 text-brand-success' : 'bg-gray-100 text-gray-600'}`}>{u.status}</Badge></td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(u)} className="h-8 w-8" data-testid={`edit-trader-${u.id}`}><Pencil size={14} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(u.id)} className="h-8 w-8 text-brand-error" data-testid={`delete-trader-${u.id}`}><Trash2 size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="trader-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? 'Edit Trader' : 'New Trader'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
            <div className="space-y-2"><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="trader-name-input" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="space-y-2"><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="trader-email-input" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="space-y-2"><Label>Password {editing ? '(leave blank to keep)' : '*'}</Label><Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} data-testid="trader-password-input" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="trader-phone-input" className="bg-white border-brand-border rounded-sm" /></div>
            <div className="space-y-2">
              <Label>Branch</Label>
              <Select value={form.branch_id} onValueChange={v => setForm({...form, branch_id: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="trader-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="trader-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-brand-border rounded-sm">Cancel</Button>
            <Button onClick={handleSave} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="trader-save-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
